/**
 * Saya Matching Popup
 * Intercepts add-to-cart on knife products that have matching sayas/guards,
 * shows a popup offering the accessory, then continues to cart/checkout.
 *
 * Works regardless of the theme's afterAtc setting ('page', 'drawer', etc.).
 */

(function () {
  'use strict';

  // ── Helpers ───────────────────────────────────────────────────────────────

  function formatMoney(cents) {
    return (cents / 100).toLocaleString('en-AU', {
      style: 'currency',
      currency: 'AUD',
    });
  }

  // Parse discount strings like "15%" or "$20" and apply to a price (in cents).
  // Returns { discountedPrice, savedCents, label }.
  function applyDiscount(priceCents, discountStr) {
    if (!discountStr) return { discountedPrice: priceCents, savedCents: 0, label: '' };
    const t = String(discountStr).trim();

    if (t.endsWith('%')) {
      const pct = parseFloat(t.slice(0, -1));
      if (!isNaN(pct) && pct > 0 && pct <= 100) {
        const savedCents = Math.round((priceCents * pct) / 100);
        return {
          discountedPrice: priceCents - savedCents,
          savedCents: savedCents,
          label: `${pct}% off`,
        };
      }
    }

    if (t.startsWith('$')) {
      const amount = parseFloat(t.slice(1));
      if (!isNaN(amount) && amount > 0) {
        const savedCents = Math.min(Math.round(amount * 100), priceCents);
        return {
          discountedPrice: priceCents - savedCents,
          savedCents: savedCents,
          label: `$${amount} off`,
        };
      }
    }

    return { discountedPrice: priceCents, savedCents: 0, label: '' };
  }

  function getMatchingData(productId) {
    const el = document.getElementById('saya-data-' + productId);
    if (!el) return null;
    try {
      const data = JSON.parse(el.textContent);
      return Array.isArray(data) && data.length ? data : null;
    } catch (err) {
      console.error('[saya-popup] Failed to parse matching data:', err);
      return null;
    }
  }

  // ── Build popup HTML ──────────────────────────────────────────────────────

  function buildPopupHTML(items) {
    const itemsHTML = items.map((item) => {
      const imageHTML = item.image
        ? `<img class="saya-popup__item-image" src="${item.image}" alt="${escapeHtml(item.title)}" width="72" height="72" loading="lazy">`
        : `<div class="saya-popup__item-image--placeholder">
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="32" height="32">
               <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
             </svg>
           </div>`;

      // Multi-variant companions: show "From $X" using the lowest variant price
      // and link to the product page instead of an Add button.
      const isMultiVariant = (item.variants_count || 1) > 1;
      const basePriceCents = isMultiVariant && item.price_min != null ? item.price_min : item.price;
      const fromPrefix = isMultiVariant ? 'From ' : '';

      const { discountedPrice, savedCents, label } = applyDiscount(basePriceCents, item.discount);
      const priceHTML = label
        ? `<span class="saya-popup__item-compare-price">${fromPrefix}${formatMoney(basePriceCents)}</span> <span class="saya-popup__item-discount-price">${fromPrefix}${formatMoney(discountedPrice)}</span>`
        : `<span class="saya-popup__item-discount-price">${fromPrefix}${formatMoney(basePriceCents)}</span>`;

      const badgeHTML = label
        ? `<span class="saya-popup__item-badge">${escapeHtml(label)}</span>`
        : (item.type ? `<span class="saya-popup__item-badge">${escapeHtml(item.type)}</span>` : '');

      const unavailableHTML = !item.available
        ? `<span class="saya-popup__item-unavailable">Out of stock</span>`
        : '';

      return `
        <div class="saya-popup__item">
          ${imageHTML}
          <div class="saya-popup__item-info">
            ${badgeHTML}
            <p class="saya-popup__item-name">${escapeHtml(item.title)}</p>
            <p class="saya-popup__item-price">${priceHTML}</p>
            ${unavailableHTML}
          </div>
          ${isMultiVariant
            ? `<a class="saya-popup__view-options-btn" href="${escapeHtml(item.url)}" target="_blank" rel="noopener" aria-label="View options for ${escapeHtml(item.title)}">
                 View options
                 <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"/>
                 </svg>
               </a>`
            : (item.available
                ? `<button class="saya-popup__atc-btn" data-variant-id="${item.variant_id}" aria-label="Add ${escapeHtml(item.title)} to cart">
                     <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                     </svg>
                     Add
                   </button>`
                : '')}
        </div>`;
    }).join('');

    return `
      <div class="saya-popup-overlay is-hidden" id="saya-popup-overlay" role="dialog" aria-modal="true" aria-labelledby="saya-popup-title">
        <div class="saya-popup">
        <div class="saya-popup__inner">
          <div class="saya-popup__header">
            <div>
              <h2 class="saya-popup__title" id="saya-popup-title">Complete your knife</h2>
              <p class="saya-popup__subtitle">Get a matching accessory and save instantly at checkout!</p>
            </div>
            <button class="saya-popup__close" id="saya-popup-close" aria-label="Close">&times;</button>
          </div>
          <div class="saya-popup__items">${itemsHTML}</div>
          <div class="saya-popup__footer">
            <button class="saya-popup__skip" id="saya-popup-skip">No thanks, continue to cart</button>
          </div>
        </div>
        </div>
      </div>`;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ── Show / hide ───────────────────────────────────────────────────────────

  function showPopup(items, onDismiss) {
    const existing = document.getElementById('saya-popup-overlay');
    if (existing) existing.remove();

    document.body.insertAdjacentHTML('beforeend', buildPopupHTML(items));

    const overlay = document.getElementById('saya-popup-overlay');

    function close() {
      overlay.classList.remove('is-visible');
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
      overlay.addEventListener('transitionend', () => {
        overlay.remove();
        onDismiss && onDismiss();
      }, { once: true });
    }

    function onKeyDown(e) {
      if (e.key === 'Escape') close();
    }

    document.getElementById('saya-popup-close').addEventListener('click', close);
    document.getElementById('saya-popup-skip').addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', onKeyDown);

    overlay.querySelectorAll('.saya-popup__atc-btn').forEach((btn) => {
      btn.addEventListener('click', () => onSayaAddToCart(btn, close));
    });

    // Animate in
    requestAnimationFrame(() => {
      overlay.classList.remove('is-hidden');
      requestAnimationFrame(() => overlay.classList.add('is-visible'));
    });

    document.body.style.overflow = 'hidden';
  }

  // ── Add saya to cart ──────────────────────────────────────────────────────

  async function onSayaAddToCart(btn, closeAndProceed) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('is-loading');
    btn.innerHTML = '…';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!res.ok) throw new Error('Add to cart failed');

      btn.classList.remove('is-loading');
      btn.classList.add('is-success');
      btn.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
        </svg>
        Added!`;

      // Close popup and proceed to cart after short pause
      setTimeout(closeAndProceed, 900);
    } catch (err) {
      console.error('[saya-popup] Error adding saya to cart:', err);
      btn.classList.remove('is-loading');
      btn.textContent = 'Try again';
    }
  }

  // ── Intercept add-to-cart ─────────────────────────────────────────────────

  function init() {
    const productForm = document.querySelector('product-form[data-product-id]');
    if (!productForm) return;

    const productId = productForm.dataset.productId;
    const matchingData = getMatchingData(productId);

    if (!matchingData) {
      console.log('[saya-popup] No matching sayas for product', productId);
      return;
    }

    console.log('[saya-popup] Matching sayas found:', matchingData);

    const form = productForm.querySelector('.js-product-form');
    if (!form) return;

    // Intercept in capture phase so we run before the theme's submit handler
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();

      const submitBtn = form.querySelector('[name="add"]');
      if (submitBtn) {
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.classList.add('is-loading');
      }

      // Add the knife to cart first
      try {
        const formData = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });

        if (!res.ok) throw new Error('Add to cart failed');

        if (submitBtn) {
          submitBtn.classList.add('is-success');
          submitBtn.classList.remove('is-loading');
        }

        // Show saya popup at 100ms — proceed to cart on dismiss
        setTimeout(() => {
          showPopup(matchingData, () => {
            window.location.href = window.theme?.routes?.cart || '/cart';
          });
        }, 100);

      } catch (err) {
        console.error('[saya-popup] Add to cart error:', err);
        if (submitBtn) {
          submitBtn.removeAttribute('aria-disabled');
          submitBtn.classList.remove('is-loading');
        }
        // Fall back: re-submit normally
        form.submit();
      }
    }, true); // true = capture phase
  }

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
